import React, {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { NavTreeNode } from "@/utils/types";
import { useDispatch, useSelector } from "react-redux";
import {
  setHoveredElementKey,
  setShowContextMenu,
  setSelectedElementKey,
  setMultiSelectWithElementKey,
  setAllSelectedElementKeys,
  setDraggingComponent,
} from "@/store/iframeSlice";
import {
  canDragIntoNavNode,
  useActiveCanvas,
  useAutoUpdateRef,
  useCanvasId,
} from "@/utils/utils";
import {
  EMPTY_TREE_CODEBASE_ID,
  STORYBOARD_TYPES,
  SKIP_ROOT_CODEBASE_ID,
} from "@/utils/constants";
import { cn } from "@/lib/utils";
import { TempoElement } from "@/utils/tempoElement";
import { ChevronDownIcon, DiamondIcon, PlayIcon } from "lucide-react";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { getLookupKeyForComponent } from "@/utils/jsx/extractElementsUtils";
import { JsxElementType } from "@/utils/jsx/jsxElementTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import IconFromElementType from "./IconFromElementType";
import { getFullElementName } from "@/utils/getFullElementName";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { toast } from "sonner";
import { isNavTreeEmpty } from "../Canvas/Storyboard/StaticIframe/utils/navTreeLookupUtils";
import { useGetCopyDataForSelectedElements } from "../Canvas/hooks/useCopyPaste";
import { useInsertClipboardDataIntoCanvas } from "../Canvas/utils/canvasClipboardData";

/* -------------------------------------------------------------------------- */
/*                              Clean Data Types                              */
/* -------------------------------------------------------------------------- */

// Pure display data - no application concepts
type LayerItemData = {
  id: string; // Element key
  name: string; // "http://localhost:20007", "Div", "Button", "MyComponent"
  elementType?: string; // "div", "button", "img" (undefined for storyboard = play icon)
  subtitle?: string; // "bg-white w-[1013px]" - class list
  isComponent: boolean; // Purple styling + diamond icon
  level: number; // Indentation depth
  children: LayerItemData[]; // Recursive structure (empty array if no children)
  hidden?: boolean; // Hide this item (for flat storyboards)
  alwaysOpen?: boolean; // Always show children (for flat storyboards)
};

/* -------------------------------------------------------------------------- */
/*                              UI Components                                 */
/* -------------------------------------------------------------------------- */

// Create a singleton empty drag image that's preloaded once
const emptyDragImage = new Image();
emptyDragImage.src =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

type LayerItemProps = {
  item: LayerItemData;

  // Display state
  isSelected: boolean;
  isHovered: boolean;
  isOpen: boolean;
  isDragging: boolean;
  isDraggedOver: boolean;
  dragOverPosition: "top" | "bottom" | "middle" | null;
  showDragOutline: boolean;
  showChevron: boolean;
  isDraggable: boolean; // Whether this item can be dragged

  // Event handlers
  onClick: (id: string, e: React.MouseEvent) => void;
  onDoubleClick: (id: string) => void;
  onContextMenu: (id: string, e: React.MouseEvent) => void;
  onMouseOver: (id: string) => void;
  onToggleOpen: (id: string, e: React.MouseEvent) => void;

  // Drag handlers
  onDragStart: (id: string, e: React.DragEvent) => void;
  onDragEnd: (id: string, e: React.DragEvent) => void;
  onDragOver: (id: string, e: React.DragEvent) => void;
  onDragLeave: (id: string, e: React.DragEvent) => void;
  onDrop: (id: string, e: React.DragEvent) => void;

  // Ref callback
  elementRef?: (id: string, ref: HTMLDivElement | null) => void;
};

const LayerItem = React.memo((props: LayerItemProps) => {
  const {
    item,
    isSelected,
    isHovered,
    isOpen,
    isDragging,
    isDraggedOver,
    dragOverPosition,
    showDragOutline,
    showChevron,
    isDraggable,
    onClick,
    onDoubleClick,
    onContextMenu,
    onMouseOver,
    onToggleOpen,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
    elementRef,
  } = props;

  const rowStyles = cn(
    "min-w-full flex h-8 rounded-sm font-normal flex-row items-center cursor-pointer whitespace-nowrap text-xs relative",
  );

  const styleToSet: React.CSSProperties = {};
  if (isDragging) {
    styleToSet.opacity = 1;
  }

  const hasChildren = item.children.length > 0;

  // Don't render if hidden
  if (item.hidden) {
    return null;
  }

  return (
    <div
      draggable={isDraggable}
      className={cn(
        rowStyles,
        isSelected || isHovered ? "bg-background-secondary" : "",
        "select-none",
      )}
      onDoubleClick={() => onDoubleClick(item.id)}
      onMouseOver={() => onMouseOver(item.id)}
      onClick={(e) => {
        e.stopPropagation();
        onClick(item.id, e);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(item.id, e);
      }}
      onDragStart={(e) => {
        // Use preloaded transparent 1x1 image, offset way off screen
        e.dataTransfer.setDragImage(emptyDragImage, 9999, 9999);
        e.dataTransfer.effectAllowed = "move";
        onDragStart(item.id, e);
      }}
      onDragEnd={(e) => onDragEnd(item.id, e)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(item.id, e);
      }}
      onDragLeave={(e) => onDragLeave(item.id, e)}
      onDrop={(e) => onDrop(item.id, e)}
      style={styleToSet}
    >
      {showDragOutline && (
        <div className="absolute top-0 left-0 w-full h-full border border-primary-200 z-900" />
      )}
      <div
        className={cn(
          rowStyles,
          "",
          item.isComponent ? "font-medium text-primary-n-500" : "text-white",
        )}
        draggable={false}
      >
        <div
          draggable={false}
          style={{
            flexShrink: 0,
            marginLeft: `${item.level * 1.2}rem`,
            height: "0px",
          }}
        />
        <div
          className={cn(
            "relative flex h-full w-full flex-row items-center border-transparent border-y-2",
            isDraggedOver &&
              dragOverPosition === "top" &&
              "border-t-primary-200",
            isDraggedOver &&
              dragOverPosition === "bottom" &&
              "border-b-primary-200",
          )}
        >
          {hasChildren ? (
            <ChevronDownIcon
              className={cn(
                "p-1 cursor-pointer max-w-none shrink text-icons-secondary w-5 h-5",
                !isOpen && "rotate-270",
                showChevron ? "opacity-100" : "opacity-0",
              )}
              width={10}
              height={10}
              onClick={(e) => {
                e.stopPropagation();
                onToggleOpen(item.id, e);
              }}
            />
          ) : (
            <div draggable={false} className="w-5 h-5 p-1" />
          )}

          {item.isComponent && (
            <DiamondIcon className="max-w-none w-3 h-3 mr-1" />
          )}

          {/* Show play icon for storyboards (no elementType) */}
          {!item.elementType && item.level === 0 && (
            <PlayIcon className="h-5 w-5 bg-secondary rounded-sm text-icons-secondary p-1 mr-2" />
          )}

          <div
            draggable={false}
            className={cn("text-muted-foreground flex items-center gap-2", {
              "pl-1 text-primary-n-500": item.isComponent,
              "text-primary font-medium": item.level === 0, // Storyboards and root items in flat storyboards
            })}
            ref={(ref) => elementRef?.(item.id, ref)}
          >
            {!item.isComponent && item.elementType ? (
              <IconFromElementType elementType={item.elementType} />
            ) : null}
            <span>{item.name}</span>
          </div>
          {item.subtitle && (
            <div
              draggable={false}
              className="pl-2 text-text-tertiary shrink flex text-xs"
            >
              {item.subtitle}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

LayerItem.displayName = "LayerItem";

/* -------------------------------------------------------------------------- */
/*                              LayersView Container                          */
/* -------------------------------------------------------------------------- */

type LayersViewProps = {
  // Data
  items: LayerItemData[];

  // Selection state
  selectedIds: Set<string>;
  hoveredId: string | null;

  // Open state
  openIds: Set<string>;

  // Drag state
  draggingId: string | null;
  dragOverId: string | null;
  dragOverPosition: "top" | "bottom" | "middle" | null;

  // Compute draggable state
  isItemDraggable: (id: string) => boolean;

  // Handlers
  onItemClick: (id: string, e: React.MouseEvent) => void;
  onItemDoubleClick: (id: string) => void;
  onItemContextMenu: (id: string, e: React.MouseEvent) => void;
  onItemMouseOver: (id: string) => void;
  onItemToggleOpen: (id: string, e: React.MouseEvent) => void;

  onItemDragStart: (id: string, e: React.DragEvent) => void;
  onItemDragEnd: (id: string, e: React.DragEvent) => void;
  onItemDragOver: (id: string, e: React.DragEvent) => void;
  onItemDragLeave: (id: string, e: React.DragEvent) => void;
  onItemDrop: (id: string, e: React.DragEvent) => void;

  // Container handlers
  onClearSelection: () => void;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
  onMouseOver: (e: React.MouseEvent) => void;
  onScroll: () => void;

  onElementRef: (id: string, ref: HTMLDivElement | null) => void;
};

export const LayersView = React.memo((props: LayersViewProps) => {
  const {
    items,
    selectedIds,
    hoveredId,
    openIds,
    draggingId,
    dragOverId,
    dragOverPosition,
    isItemDraggable,
    onItemClick,
    onItemDoubleClick,
    onItemContextMenu,
    onItemMouseOver,
    onItemToggleOpen,
    onItemDragStart,
    onItemDragEnd,
    onItemDragOver,
    onItemDragLeave,
    onItemDrop,
    onClearSelection,
    onMouseLeave,
    onMouseEnter,
    onMouseOver,
    onScroll,
    onElementRef,
  } = props;

  const [domTreeHovered, setDomTreeHovered] = useState(false);

  // Recursively render tree
  const renderItem = (item: LayerItemData): React.ReactNode => {
    const isSelected = selectedIds.has(item.id);
    const isHovered = hoveredId === item.id;
    const isOpen = item.alwaysOpen || openIds.has(item.id); // Always open if alwaysOpen flag set
    const isDragging = draggingId === item.id;
    const isDraggedOver = dragOverId === item.id;

    // Check if dropping inside this item (one of its children is dragOverId)
    let droppingInside = false;
    if (draggingId && dragOverPosition !== "middle") {
      // Only highlight parents in the same storyboard as the dragging element
      const draggingStoryboardId =
        TempoElement.fromKey(draggingId).storyboardId;
      const itemStoryboardId = TempoElement.fromKey(item.id).storyboardId;

      if (draggingStoryboardId === itemStoryboardId) {
        item.children.forEach((child) => {
          if (child.id === dragOverId) {
            droppingInside = true;
          }
        });
      }
    }

    const showDragOutline =
      (isDraggedOver && dragOverPosition === "middle") || droppingInside;
    const isDraggable = isItemDraggable(item.id);

    const itemElement = (
      <LayerItem
        key={item.id}
        item={item}
        isSelected={isSelected}
        isHovered={isHovered}
        isOpen={isOpen}
        isDragging={isDragging}
        isDraggedOver={isDraggedOver}
        dragOverPosition={isDraggedOver ? dragOverPosition : null}
        showDragOutline={showDragOutline}
        showChevron={domTreeHovered}
        isDraggable={isDraggable}
        onClick={onItemClick}
        onDoubleClick={onItemDoubleClick}
        onContextMenu={onItemContextMenu}
        onMouseOver={onItemMouseOver}
        onToggleOpen={onItemToggleOpen}
        onDragStart={onItemDragStart}
        onDragEnd={onItemDragEnd}
        onDragOver={onItemDragOver}
        onDragLeave={onItemDragLeave}
        onDrop={onItemDrop}
        elementRef={onElementRef}
      />
    );

    // Render children if open (each child computes its own droppingInside)
    const children =
      isOpen && item.children.length > 0 ? (
        <Fragment key={`${item.id}-children`}>
          {item.children.map((child) => renderItem(child))}
        </Fragment>
      ) : null;

    return (
      <Fragment key={item.id}>
        {itemElement}
        {children}
      </Fragment>
    );
  };

  return (
    <div className="flex w-full flex-col h-full">
      <div className="px-3 h-10 flex items-center shrink-0">
        <span className="text-xs font-bold text-white leading-none">
          Layers
        </span>
      </div>
      <div
        className="w-full flex-1 min-h-0 overflow-scroll select-none"
        onClick={() => onClearSelection()}
        onMouseLeave={() => {
          setDomTreeHovered(false);
          onMouseLeave();
        }}
        onMouseOver={(e) => {
          onMouseOver(e);
        }}
        onMouseEnter={() => {
          setDomTreeHovered(true);
          onMouseEnter();
        }}
        onScroll={() => onScroll()}
      >
        <div className="inline-block min-w-full">
          <div className="flex w-full flex-col pt-1">
            {items.map((item) => renderItem(item))}
          </div>
        </div>
      </div>
    </div>
  );
});

LayersView.displayName = "LayersView";

/* -------------------------------------------------------------------------- */
/*                              Layers Wrapper                                */
/* -------------------------------------------------------------------------- */

const Layers = React.memo(() => {
  const canvasId = useCanvasId();

  const { hoveredNavTreeNode, hoveredElement } = useHoveredNavTreeNode();
  const { selectedElement, selectedNavTreeNode, storyboardIdSelected } =
    useSelectedNavTreeNode();
  const { multiselectedElements, multiselectedNavTreeNodes } =
    useMultiselectedNavTreeNode();

  const [isDragging, setIsDragging] = useState(false);
  const [dragOverElementKey, setDragOverElementKey] = useState<string | null>(
    null,
  );
  const [dragOverPosition, setDragOverPosition] = useState<
    "top" | "bottom" | "middle" | null
  >(null);

  const [previousMultiselectedElementKey, setPreviousMultiselectedElementKey] =
    useState<string | null>(null);

  const [storyboardToOpen, setStoryboardToOpen] = useState<
    Record<string, boolean>
  >({});

  const dispatch = useDispatch<any>();

  const canvas = useActiveCanvas();

  const storyboardIdToType = useCanvasStore(
    (state) => state.storyboardIdToType,
  );

  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl,
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  const isFlushing = useDesignCommandManager((state) => state.isFlushing);

  const nodeOpenDict = useCanvasStore((state) => state.nodeOpenDict);
  const onClickNodeOpen = useCanvasStore((state) => state.onClickNodeOpen);

  // For auto-scrolling to element
  const [lastSelectedScrolledTo, setLastSelectedScrolledTo] = useState<
    string | null
  >(null);
  const [selectedElementFromLayersPanel, setSelectedElementFromLayersPanel] =
    useState<string | null>(null);
  const elementKeyToRef = useRef<Record<string, HTMLDivElement | null>>({});

  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree,
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (store) => store.elementKeyToNavNodeLookup,
  );

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  const getCopyDataForSelectedElements = useGetCopyDataForSelectedElements();
  const { insertClipboardDataIntoCanvas } = useInsertClipboardDataIntoCanvas();

  const { componentKeyToComponent } = useAssetComponents();

  const setCanvasComponentKey = useCanvasStore(
    (state) => state.setCanvasComponentKey,
  );

  const setMenu = useCanvasStore((state) => state.setMenu);

  /* ------------------------------------------------------------------------ */
  /*                         Data Transformation                              */
  /* ------------------------------------------------------------------------ */

  // Transform NavTreeNode → LayerItemData
  const transformNode = useCallback(
    (node: NavTreeNode, level: number): LayerItemData => {
      return {
        id: node.tempoElement.getKey(),
        name: node.isComponent
          ? (node.name ?? "Unknown")
          : getFullElementName(node.name),
        elementType: node.name?.toLowerCase(),
        subtitle: node.classList?.length ? node.classList.join(" ") : undefined,
        isComponent: Boolean(node.isComponent),
        level: level,
        children:
          node.children?.map((child) => transformNode(child, level + 1)) || [],
      };
    },
    [],
  );

  // Transform storyboards and their nav trees to LayerItemData
  const items = useMemo(() => {
    if (!canvas?.storyboards) return [];

    return canvas.storyboards
      .filter((storyboard: any) => storyboard.deleted !== true)
      .map((storyboard: any) => {
        const navTree = storyboardToNavTree?.[storyboard.id];
        const isEmpty = !navTree || isNavTreeEmpty(navTree);

        let name = storyboard.id;
        if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
          name = devServerUrl || "Application";
        } else {
          name = storyboard.name;
        }

        const storyboardType = storyboardIdToType[storyboard.id];
        const flatStoryboard =
          !isEmpty &&
          storyboardType &&
          storyboardType !== STORYBOARD_TYPES.APPLICATION;

        let children: LayerItemData[] = [];
        if (!isEmpty && navTree) {
          // Skip the root if it's SKIP_ROOT_CODEBASE_ID
          if (navTree.tempoElement.codebaseId === SKIP_ROOT_CODEBASE_ID) {
            children =
              navTree.children?.map((child) =>
                transformNode(child, flatStoryboard ? 0 : 1),
              ) || [];
          } else {
            children = [transformNode(navTree, flatStoryboard ? 0 : 1)];
          }
        }

        return {
          id: TempoElement.forStoryboard(storyboard.id).getKey(),
          name: name,
          elementType: undefined,
          subtitle: undefined,
          isComponent: false,
          level: 0,
          children: children,
          hidden: flatStoryboard, // Hide storyboard header for flat storyboards
          alwaysOpen: flatStoryboard, // Always show children for flat storyboards
        };
      });
  }, [
    canvas,
    storyboardToNavTree,
    devServerUrl,
    storyboardIdToType,
    transformNode,
  ]);

  // Unify selection state
  const selectedIds = useMemo(() => {
    const ids = new Set<string>();
    if (!selectedElement.isEmpty()) {
      ids.add(selectedElement.getKey());
    }
    multiselectedElements?.forEach((elem) => {
      ids.add(elem.getKey());
    });
    return ids;
  }, [selectedElement, multiselectedElements]);

  // Unify open state
  const openIds = useMemo(() => {
    const ids = new Set<string>();
    Object.entries(nodeOpenDict).forEach(([key, isOpen]) => {
      if (isOpen) ids.add(key);
    });
    Object.entries(storyboardToOpen).forEach(([id, isOpen]) => {
      if (isOpen) ids.add(id);
    });
    return ids;
  }, [nodeOpenDict, storyboardToOpen]);

  // Track flat storyboards and their root-level children for special selection behavior
  const flatStoryboardInfo = useMemo(() => {
    const info = new Map<string, string>(); // childId -> storyboardId
    items.forEach((storyboard) => {
      if (storyboard.alwaysOpen) {
        // This is a flat storyboard, track its direct children
        storyboard.children.forEach((child) => {
          if (child.level === 0) {
            info.set(child.id, storyboard.id);
          }
        });
      }
    });
    return info;
  }, [items]);

  /* ------------------------------------------------------------------------ */
  /*                         Selection Handlers                               */
  /* ------------------------------------------------------------------------ */

  const handleItemClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      // Check if this is a root-level child of a flat storyboard - if so, select the storyboard instead
      let idToSelect = id;
      const flatStoryboardId = flatStoryboardInfo.get(id);
      if (flatStoryboardId) {
        idToSelect = flatStoryboardId;
      }

      const tempoElement = TempoElement.fromKey(idToSelect);

      // Shift click - range select
      if (e.shiftKey && !selectedElement.isEmpty()) {
        const keyToSelectFrom = previousMultiselectedElementKey || id;
        const previousTempoElement = TempoElement.fromKey(keyToSelectFrom);

        // Not siblings, return
        if (!previousTempoElement.isSiblingOf(tempoElement)) {
          return;
        }

        const newMultiselectedElementKeys = multiselectedElements?.map((elem) =>
          elem.getKey(),
        ) || [selectedElement.getKey()];

        if (tempoElement.isStoryboard()) {
          const orderedStoryboards =
            canvas?.storyboards
              .filter((storyboard: any) => storyboard.deleted !== true)
              .map((storyboard: any) => storyboard.id) || [];

          const prevIndex = orderedStoryboards.indexOf(
            previousTempoElement.storyboardId,
          );
          const currentIndex = orderedStoryboards.indexOf(
            tempoElement.storyboardId,
          );

          for (
            let i = Math.min(prevIndex, currentIndex);
            i <= Math.max(prevIndex, currentIndex);
            i++
          ) {
            const newKeyToAdd = TempoElement.forStoryboard(
              orderedStoryboards[i],
            ).getKey();
            if (!newMultiselectedElementKeys.includes(newKeyToAdd)) {
              newMultiselectedElementKeys.push(newKeyToAdd);
            }
          }
        } else {
          const navNodeToSelectFrom =
            elementKeyToNavNodeLookup[keyToSelectFrom];
          const thisNavNode = elementKeyToNavNodeLookup[id];

          if (!navNodeToSelectFrom?.parent || !thisNavNode?.parent) {
            return;
          }

          const navNodeToSelectFromIndex =
            navNodeToSelectFrom.parent.children.findIndex((child) =>
              child.tempoElement.isEqual(navNodeToSelectFrom.tempoElement),
            );
          const thisNavNodeIndex = thisNavNode.parent.children.findIndex(
            (child) => child.tempoElement.isEqual(thisNavNode.tempoElement),
          );

          for (
            let i = Math.min(navNodeToSelectFromIndex, thisNavNodeIndex);
            i <= Math.max(navNodeToSelectFromIndex, thisNavNodeIndex);
            i++
          ) {
            const newKeyToAdd =
              thisNavNode.parent.children[i].tempoElement.getKey();
            if (!newMultiselectedElementKeys.includes(newKeyToAdd)) {
              newMultiselectedElementKeys.push(newKeyToAdd);
            }
          }
        }

        dispatch(
          setAllSelectedElementKeys({
            multiSelectedElementKeys: newMultiselectedElementKeys,
            selectedElementKey: selectedElement.getKey(),
          }),
        );

        setPreviousMultiselectedElementKey(id);
        return;
      }

      // Meta click - toggle multi-select
      if (e.metaKey) {
        dispatch(setMultiSelectWithElementKey(id));
        setPreviousMultiselectedElementKey(id);
      } else if (!selectedElement.isEqual(tempoElement)) {
        setSelectedElementFromLayersPanel(idToSelect);
        dispatch(setSelectedElementKey(idToSelect));
        setPreviousMultiselectedElementKey(id);
      }
    },
    [
      selectedElement,
      multiselectedElements,
      previousMultiselectedElementKey,
      canvas,
      elementKeyToNavNodeLookup,
      dispatch,
      flatStoryboardInfo,
    ],
  );

  const handleItemDoubleClick = useCallback(
    (id: string) => {
      const node = elementKeyToNavNodeLookup[id];
      if (!node) return;

      // Navigate to component source if this is a component instance
      if (node.isComponent && treeElementLookup && componentKeyToComponent) {
        const componentInstance =
          treeElementLookup?.[node.tempoElement.codebaseId];
        if (componentInstance?.type === JsxElementType.ComponentInstance) {
          const key = getLookupKeyForComponent(componentInstance);
          const sourceComponent = componentKeyToComponent[key];
          if (sourceComponent) {
            setCanvasComponentKey(key);
          }
        }
      }
    },
    [
      elementKeyToNavNodeLookup,
      treeElementLookup,
      componentKeyToComponent,
      setCanvasComponentKey,
    ],
  );

  const handleItemContextMenu = useCallback(
    (id: string, e: React.MouseEvent) => {
      const tempoElement = TempoElement.fromKey(id);

      const nodeInMultiselected = multiselectedElements?.find((elem) =>
        elem.isEqual(tempoElement),
      );
      if (!nodeInMultiselected) {
        dispatch(setSelectedElementKey(id));
      }

      dispatch(setShowContextMenu(true));
      setMenu(e.clientX, e.clientY);
    },
    [multiselectedElements, dispatch, setMenu],
  );

  const handleItemMouseOver = useCallback(
    (id: string) => {
      dispatch(setHoveredElementKey(id));
    },
    [dispatch],
  );

  const handleItemToggleOpen = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();

      const tempoElement = TempoElement.fromKey(id);

      // Check if this is a storyboard (level 0, no parent in nav tree)
      if (tempoElement.isStoryboard()) {
        setStoryboardToOpen({
          ...storyboardToOpen,
          [id]: !storyboardToOpen[id],
        });
      } else {
        onClickNodeOpen(tempoElement);
      }
    },
    [storyboardToOpen, onClickNodeOpen],
  );

  /* ------------------------------------------------------------------------ */
  /*                         Drag & Drop Handlers                             */
  /* ------------------------------------------------------------------------ */

  const handleItemDragStart = useCallback(
    (id: string, e: React.DragEvent) => {
      dispatch(setDraggingComponent("nav"));
      setIsDragging(true);

      const tempoElement = TempoElement.fromKey(id);
      if (!selectedElement.isEqual(tempoElement)) {
        dispatch(setSelectedElementKey(id));
      }
    },
    [dispatch, selectedElement],
  );

  const handleItemDragEnd = useCallback(
    (id: string, e: React.DragEvent) => {
      setIsDragging(false);
      setDragOverElementKey(null);
      setDragOverPosition(null);
      dispatch(setDraggingComponent(null));
    },
    [dispatch],
  );

  const handleItemDragOver = useCallback(
    (id: string, e: React.DragEvent) => {
      if (!isDragging || isFlushing) return;

      // Can't drag over root-level items in flat storyboards (they have no draggable parent)
      if (flatStoryboardInfo.has(id)) {
        return;
      }

      const node = elementKeyToNavNodeLookup[id];
      if (!node) return;

      const draggingNode = selectedNavTreeNode;
      if (!draggingNode) return;

      // Can't drop into same element
      if (draggingNode.tempoElement.getKey() === id) return;

      e.preventDefault();
      setDragOverElementKey(id);

      const targetHeight = e.currentTarget.getBoundingClientRect().height;
      const offsetY = e.clientY - e.currentTarget.getBoundingClientRect().top;

      // Can't move into an img tag, but can move around it
      if (!canDragIntoNavNode(node)) {
        if (offsetY < targetHeight / 2) {
          setDragOverPosition("top");
        } else {
          setDragOverPosition("bottom");
        }
      } else {
        if (offsetY < targetHeight / 3) {
          setDragOverPosition("top");
        } else if (
          offsetY >= targetHeight / 3 &&
          offsetY < (targetHeight / 3) * 2
        ) {
          setDragOverPosition("middle");
        } else {
          setDragOverPosition("bottom");
        }
      }
    },
    [
      isDragging,
      isFlushing,
      elementKeyToNavNodeLookup,
      selectedNavTreeNode,
      flatStoryboardInfo,
    ],
  );

  const handleItemDragLeave = useCallback(
    (id: string, e: React.DragEvent) => {
      if (dragOverElementKey === id) {
        setDragOverElementKey(null);
        setDragOverPosition(null);
      }
    },
    [dragOverElementKey],
  );

  const handleItemDrop = useCallback(
    async (id: string, e: React.DragEvent) => {
      const node = elementKeyToNavNodeLookup[id];
      const draggingNode = selectedNavTreeNode;

      if (!node || !draggingNode) return;

      // Can't drop into same element
      if (draggingNode.tempoElement.getKey() === id) return;

      // Dragging into parent, do nothing
      if (draggingNode.parent === node && dragOverPosition !== "top") return;

      const copyData = await getCopyDataForSelectedElements();
      if (!copyData) return;

      if (storyboardIdSelected) {
        copyData.deleteSourceStoryboard = true;
      } else {
        copyData.deleteSourceElements = true;
      }

      let tempoElementToDropInto: TempoElement | undefined = undefined;
      let afterCodebaseId: string | undefined = undefined;
      let beforeCodebaseId: string | undefined = undefined;

      if (dragOverPosition === "middle") {
        tempoElementToDropInto = node.tempoElement;
        if (node.children?.length) {
          const lastChild = node.children[node.children.length - 1];
          afterCodebaseId = lastChild.tempoElement.codebaseId;
        }

        if (!nodeOpenDict[id]) {
          onClickNodeOpen(node.tempoElement);
        }
      } else {
        if (!node.parent) {
          toast.error("No parent found to drop in to");
          return;
        }

        tempoElementToDropInto = node.parent.tempoElement;

        let nodeIndexInParent: number | undefined = undefined;
        let droppingNodeIndexInParent: number | undefined = undefined;

        for (let i = 0; i < (node.parent.children?.length ?? 0); i++) {
          if (node.parent.children[i].tempoElement.isEqual(node.tempoElement)) {
            nodeIndexInParent = i;
          }
          if (
            node.parent.children[i].tempoElement.isEqual(
              draggingNode.tempoElement,
            )
          ) {
            droppingNodeIndexInParent = i;
          }
        }

        if (dragOverPosition === "top") {
          if (
            droppingNodeIndexInParent !== undefined &&
            droppingNodeIndexInParent === nodeIndexInParent
          ) {
            return;
          }

          const nodeAtIndex = nodeIndexInParent
            ? node.parent.children[nodeIndexInParent - 1]
            : null;

          afterCodebaseId = nodeAtIndex?.tempoElement.codebaseId;
          beforeCodebaseId = node.tempoElement.codebaseId;
        } else {
          if (
            droppingNodeIndexInParent !== undefined &&
            nodeIndexInParent !== undefined &&
            droppingNodeIndexInParent === nodeIndexInParent + 1
          ) {
            return;
          }

          afterCodebaseId = node.tempoElement.codebaseId;
          beforeCodebaseId =
            nodeIndexInParent !== undefined &&
            nodeIndexInParent < node.parent.children.length - 1
              ? node.parent.children[nodeIndexInParent + 1].tempoElement
                  .codebaseId
              : undefined;
        }
      }

      if (!tempoElementToDropInto) return;

      const copyDataWithDropPosition = {
        ...copyData,
        insertIntoPosition: {
          parentTempoElement: tempoElementToDropInto,
          beforeCodebaseId: beforeCodebaseId,
          afterCodebaseId: afterCodebaseId,
        },
      };

      insertClipboardDataIntoCanvas(copyDataWithDropPosition);

      setIsDragging(false);
      setDragOverElementKey(null);
      setDragOverPosition(null);
    },
    [
      elementKeyToNavNodeLookup,
      selectedNavTreeNode,
      dragOverPosition,
      getCopyDataForSelectedElements,
      storyboardIdSelected,
      nodeOpenDict,
      onClickNodeOpen,
      insertClipboardDataIntoCanvas,
    ],
  );

  /* ------------------------------------------------------------------------ */
  /*                         Container Handlers                               */
  /* ------------------------------------------------------------------------ */

  const handleClearSelection = useCallback(() => {
    dispatch(setSelectedElementKey(null));
  }, [dispatch]);

  const handleMouseLeave = useCallback(() => {
    dispatch(setHoveredElementKey(null));
  }, [dispatch]);

  const handleMouseEnter = useCallback(() => {
    // Nothing special needed
  }, []);

  const handleMouseOver = useCallback(
    (e: React.MouseEvent) => {
      // Check if drag ended unexpectedly
      if (!e.buttons && isDragging) {
        setIsDragging(false);
        setDragOverElementKey(null);
        setDragOverPosition(null);
        dispatch(setDraggingComponent(null));
      }
    },
    [isDragging, dispatch],
  );

  const handleScroll = useCallback(() => {
    setLastSelectedScrolledTo(null);
  }, []);

  const handleElementRef = useCallback(
    (id: string, ref: HTMLDivElement | null) => {
      elementKeyToRef.current[id] = ref;
    },
    [],
  );

  const isItemDraggable = useCallback(
    (id: string) => {
      // Can't drag if in multiselect mode
      if (multiselectedElements?.length) {
        return false;
      }

      // Can only drag if element has a codebaseId
      const node = elementKeyToNavNodeLookup[id];
      if (!node) {
        return false;
      }

      return Boolean(node.tempoElement.codebaseId);
    },
    [multiselectedElements, elementKeyToNavNodeLookup],
  );

  /* ------------------------------------------------------------------------ */
  /*                              Effects                                     */
  /* ------------------------------------------------------------------------ */

  // Auto-scroll to selected element
  useEffect(() => {
    if (
      selectedElement.isEmpty() ||
      !storyboardToNavTree[selectedElement.storyboardId]
    ) {
      return;
    }

    // Don't auto-scroll if selection came from clicking in the layers panel
    if (selectedElement.getKey() === selectedElementFromLayersPanel) {
      return;
    }

    if (selectedElement.getKey() === lastSelectedScrolledTo) {
      return;
    }

    const refToUse = elementKeyToRef.current[selectedElement.getKey()];
    if (!refToUse) {
      return;
    }

    refToUse.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    setLastSelectedScrolledTo(selectedElement.getKey());
  }, [selectedElement, storyboardToNavTree]);

  // Auto-open storyboard when element selected
  useEffect(() => {
    if (!storyboardToNavTree?.[selectedElement.storyboardId]) {
      return;
    }

    const navTree = storyboardToNavTree[selectedElement.storyboardId];

    let selectedNode: NavTreeNode | null = null;

    const searchNode = (node: NavTreeNode) => {
      if (!node || selectedNode) return;

      if (node?.tempoElement?.isEqual?.(selectedElement)) {
        selectedNode = node;
        return;
      }

      if (node.children) {
        node.children.forEach((child) => {
          searchNode(child);
        });
      }
    };
    searchNode(navTree);

    if (selectedNode) {
      setStoryboardToOpen({
        ...storyboardToOpen,
        [TempoElement.forStoryboard(selectedElement.storyboardId).getKey()]:
          true,
      });
    }
  }, [selectedElement, storyboardToNavTree]);

  // Auto-open node on drag hover (after 500ms)
  useEffect(() => {
    if (
      !isDragging ||
      dragOverPosition !== "middle" ||
      !dragOverElementKey ||
      nodeOpenDict[dragOverElementKey]
    ) {
      return;
    }

    const timeoutCancelId = setTimeout(() => {
      onClickNodeOpen(TempoElement.fromKey(dragOverElementKey));
    }, 500);

    return () => {
      clearTimeout(timeoutCancelId);
    };
  }, [
    dragOverElementKey,
    isDragging,
    dragOverPosition,
    nodeOpenDict,
    onClickNodeOpen,
  ]);

  // Track previous multiselected key
  useEffect(() => {
    if (selectedElement.isEmpty()) {
      setPreviousMultiselectedElementKey(null);
    } else {
      setPreviousMultiselectedElementKey(selectedElement.getKey());
    }
  }, [selectedElement]);

  /* ------------------------------------------------------------------------ */
  /*                              Render                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <LayersView
      items={items}
      selectedIds={selectedIds}
      hoveredId={hoveredElement.getKey() || null}
      openIds={openIds}
      draggingId={isDragging ? selectedElement.getKey() : null}
      dragOverId={dragOverElementKey}
      dragOverPosition={dragOverPosition}
      isItemDraggable={isItemDraggable}
      onItemClick={handleItemClick}
      onItemDoubleClick={handleItemDoubleClick}
      onItemContextMenu={handleItemContextMenu}
      onItemMouseOver={handleItemMouseOver}
      onItemToggleOpen={handleItemToggleOpen}
      onItemDragStart={handleItemDragStart}
      onItemDragEnd={handleItemDragEnd}
      onItemDragOver={handleItemDragOver}
      onItemDragLeave={handleItemDragLeave}
      onItemDrop={handleItemDrop}
      onClearSelection={handleClearSelection}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onMouseOver={handleMouseOver}
      onScroll={handleScroll}
      onElementRef={handleElementRef}
    />
  );
});

Layers.displayName = "Layers";

export default Layers;
